import { useSmoothReorder } from '../hooks/useSmoothReorder.js'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileX2, SlidersHorizontal, FolderKanban } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useSettings } from '../hooks/useSettings'
import { getUserTimezone } from '../lib/timezone'
import { formatOCC } from '../lib/occParser'
import PrivateValue from '../components/PrivateValue'
import TradeSlideOver from '../components/TradeSlideOver'
import { useSetupTradeLog } from '../hooks/useSetupTradeLog'
import { setupTradeStats } from '../lib/setupTradeLog'
import { usePositionProjectLookup } from '../hooks/usePositionProjectLookup'
import { buildPositionWorkspaceTarget } from '../lib/positionWorkspaceRoute'

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtCurrency(val) {
  if (val == null) return '—'
  return Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtPct(val) {
  if (val == null) return '—'
  const n = Number(val)
  return (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
}

function fmtDuration(mins) {
  if (mins == null) return '—'
  if (mins < 60) return `${mins}m`
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`
  const days = Math.floor(mins / 1440)
  const hours = Math.floor((mins % 1440) / 60)
  return `${days}d ${hours}h`
}

function fmtDate(date, time, tz, instant) {
  if (!date) return '—'
  const d = new Date(instant || (date + (time ? 'T' + time : '')))
  if (isNaN(d)) return '—'
  return d.toLocaleString('en-US', {
    timeZone: tz || 'America/Chicago',
    month: '2-digit', day: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).replace(',', '')
}

// Use centralized formatOCC from occParser.js

function clr(val) {
  if (val > 0) return 'text-forest'
  if (val < 0) return 'text-ember'
  return 'text-inkMuted'
}

// ── KPI Cards ─────────────────────────────────────────────────────────────────

function KPICards({ trades, unavailable }) {
  const stats = setupTradeStats(trades)
  const cards = [
    ['Recorded net P&L', fmtCurrency(stats.netPnl)],
    ['Closed setups in statistics', unavailable ? '—' : String(stats.count)],
    ['Win rate', stats.winRate == null ? '—' : `${stats.winRate.toFixed(1)}%`],
    ['Profit factor', stats.profitFactor == null ? '—' : stats.profitFactor === Infinity ? '∞' : stats.profitFactor.toFixed(2)],
    ['Avg win / avg loss', stats.avgWinLossRatio == null ? '—' : stats.avgWinLossRatio === Infinity ? '∞' : stats.avgWinLossRatio.toFixed(2)],
  ]
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
    {cards.map(([label, value]) => <div className="card" key={label}>
      <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">{label}</div>
      <div className="font-mono text-2xl font-semibold"><PrivateValue>{value}</PrivateValue></div>
    </div>)}
  </div>
}

// ── Filter Pill ───────────────────────────────────────────────────────────────

function Pill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`font-mono text-[10px] px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
        active
          ? 'bg-forest text-white border-forest'
          : 'bg-canvas2 text-inkMuted border-border hover:text-ink'
      }`}
    >
      {label}
    </button>
  )
}

// ── Date Range Presets ────────────────────────────────────────────────────────

function fmtISO(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

function getDateRange(preset) {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const d = now.getDate()
  const today = new Date(y, m, d)

  switch (preset) {
    case 'today':
      return [fmtISO(today), fmtISO(today)]
    case 'thisWeek': {
      const day = today.getDay()
      const mon = new Date(y, m, d - (day === 0 ? 6 : day - 1))
      return [fmtISO(mon), fmtISO(today)]
    }
    case 'lastWeek': {
      const day = today.getDay()
      const thisMon = new Date(y, m, d - (day === 0 ? 6 : day - 1))
      const lastMon = new Date(thisMon.getFullYear(), thisMon.getMonth(), thisMon.getDate() - 7)
      const lastSun = new Date(thisMon.getFullYear(), thisMon.getMonth(), thisMon.getDate() - 1)
      return [fmtISO(lastMon), fmtISO(lastSun)]
    }
    case 'thisMonth':
      return [fmtISO(new Date(y, m, 1)), fmtISO(today)]
    case 'lastMonth':
      return [fmtISO(new Date(y, m - 1, 1)), fmtISO(new Date(y, m, 0))]
    case 'thisQuarter': {
      const qStart = new Date(y, Math.floor(m / 3) * 3, 1)
      return [fmtISO(qStart), fmtISO(today)]
    }
    case 'lastQuarter': {
      const cqStart = Math.floor(m / 3) * 3
      return [fmtISO(new Date(y, cqStart - 3, 1)), fmtISO(new Date(y, cqStart, 0))]
    }
    case 'thisYear':
      return [fmtISO(new Date(y, 0, 1)), fmtISO(today)]
    case 'lastYear':
      return [fmtISO(new Date(y - 1, 0, 1)), fmtISO(new Date(y - 1, 11, 31))]
    case 'last30': {
      const start = new Date(y, m, d - 30)
      return [fmtISO(start), fmtISO(today)]
    }
    case 'last90': {
      const start = new Date(y, m, d - 90)
      return [fmtISO(start), fmtISO(today)]
    }
    default:
      return ['', '']
  }
}

const DATE_PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: 'today', label: 'Today' },
  { key: 'thisWeek', label: 'This Week' },
  { key: 'lastWeek', label: 'Last Week' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'thisQuarter', label: 'This Qtr' },
  { key: 'lastQuarter', label: 'Last Qtr' },
  { key: 'thisYear', label: 'This Year' },
  { key: 'lastYear', label: 'Last Year' },
  { key: 'last30', label: 'Last 30d' },
  { key: 'last90', label: 'Last 90d' },
  { key: 'custom', label: 'Custom' },
]

// ── Column Definitions ────────────────────────────────────────────────────────

const ALL_COLUMNS = [
  { key: 'status', label: 'Status', align: 'left', defaultVisible: true },
  { key: 'account', label: 'Accounts', align: 'left', defaultVisible: true },
  { key: 'symbol', label: 'Symbol', align: 'left', defaultVisible: true },
  { key: 'side', label: 'Side', align: 'left', defaultVisible: true },
  { key: 'entry', label: 'Entry', align: 'right', defaultVisible: true },
  { key: 'exit', label: 'Exit', align: 'right', defaultVisible: true },
  { key: 'openDate', label: 'Open Date', align: 'left', defaultVisible: true },
  { key: 'closeDate', label: 'Close Date', align: 'left', defaultVisible: true },
  { key: 'qty', label: 'Qty', align: 'right', defaultVisible: true },
  { key: 'netPnl', label: 'Net P&L', align: 'right', defaultVisible: true },
  { key: 'netRoi', label: 'Net ROI', align: 'right', defaultVisible: true },
  { key: 'fees', label: 'Fees', align: 'right', defaultVisible: false },
  { key: 'stopLoss', label: 'Stop', align: 'right', defaultVisible: false },
  { key: 'risk', label: 'Risk $', align: 'right', defaultVisible: false },
  { key: 'rMultiple', label: 'R-Multiple', align: 'right', defaultVisible: false },
  { key: 'duration', label: 'Duration', align: 'right', defaultVisible: true },
  { key: 'type', label: 'Type', align: 'left', defaultVisible: true },
]

const COL_MAP = Object.fromEntries(ALL_COLUMNS.map(c => [c.key, c]))
const DEFAULT_VISIBLE = ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key)
const DEFAULT_ORDER = ALL_COLUMNS.map(c => c.key)

function getSortValue(trade, key) {
  switch (key) {
    case 'status': return Number(trade.net_pnl) || 0
    case 'account': return trade.accounts?.length || trade.account_number || ''
    case 'symbol': return trade.symbol || ''
    case 'side': return trade.side || ''
    case 'entry': return Number(trade.avg_entry_price) || 0
    case 'exit': return Number(trade.avg_exit_price) || 0
    case 'openDate': return trade.entry_date || ''
    case 'closeDate': return trade.exit_date || ''
    case 'qty': return Number(trade.quantity_total) || 0
    case 'netPnl': return Number(trade.net_pnl) || 0
    case 'netRoi': return Number(trade.pnl_percent) || 0
    case 'fees': return Number(trade.commission || 0) + Number(trade.fees || 0)
    case 'stopLoss': return Number(trade.stop_loss) || 0
    case 'risk': return Number(trade.risk_amount) || 0
    case 'rMultiple': return Number(trade.r_multiple) || 0
    case 'duration': return Number(trade.duration_minutes) || 0
    case 'type': return trade.asset_type || ''
    default: return ''
  }
}

// ── Columns Dropdown ──────────────────────────────────────────────────────────

export function ColumnsDropdown({ columnOrder, visibleColumns, setColumnOrder, setVisibleColumns, onClose }) {
  const ref = useRef(null)
  const reorder = useSmoothReorder(columnOrder, next => setColumnOrder(next))

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  function toggleColumn(key) {
    setVisibleColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    )
  }

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-1 z-50 w-56 bg-canvas border border-border rounded-xl shadow-lg py-2"
    >
      <div className="px-3 pb-1.5 mb-1 border-b border-border">
        <span className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">Columns</span>
      </div>
      <div className="max-h-[400px] overflow-y-auto">
        {columnOrder.map((key) => {
          const col = COL_MAP[key]
          if (!col) return null
          return (
            <div
              key={key}
              {...reorder.bind(key)}
              onPointerDown={event => { if (event.target.closest('[data-column-drag-handle]') || (event.metaKey && !event.target.closest('input'))) reorder.start(event, key) }}
              onClickCapture={event => reorder.consumeClick(event)}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-canvas2/50 transition-transform duration-200 ease-out motion-reduce:transition-none"
            >
              {/* Drag handle — 2×3 grid of dots */}
              <span data-column-drag-handle title="Drag to reorder column" className="grid grid-cols-2 gap-[2px] w-[8px] touch-none shrink-0 cursor-grab active:cursor-grabbing opacity-30 hover:opacity-60">
                {[...Array(6)].map((_, i) => (
                  <span key={i} className="w-[3px] h-[3px] rounded-full" style={{ background: 'rgb(var(--color-ink))' }} />
                ))}
              </span>
              <label className="flex items-center gap-2 flex-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={visibleColumns.includes(key)}
                  onChange={() => toggleColumn(key)}
                  className="accent-forest w-3.5 h-3.5"
                />
                <span className="font-mono text-xs text-ink">{col.label}</span>
              </label>
            </div>
          )
        })}
      </div>
      <div className="px-3 pt-1.5 mt-1 border-t border-border">
        <button
          onClick={() => { setVisibleColumns(DEFAULT_VISIBLE); setColumnOrder(DEFAULT_ORDER) }}
          className="font-mono text-[10px] text-inkMuted hover:text-ink underline"
        >
          Reset to Default
        </button>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function TradeLog() {
  const { settings } = useSettings()
  const userTz = getUserTimezone(settings)
  const { user } = useAuth()
  const navigate = useNavigate()
  const { projectsByTradeId } = usePositionProjectLookup()

  const { trades, groups, unresolved, nicknameMap, loading, error, reload } = useSetupTradeLog()
  const [reviewView, setReviewView] = useState('setups')

  // Filters
  const [datePreset, setDatePreset] = useState('all')
  const [dateStart, setDateStart] = useState('')
  const [dateEnd, setDateEnd] = useState('')
  const [selectedAccounts, setSelectedAccounts] = useState([])
  const [sideFilter, setSideFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')

  // Sort
  const [sortKey, setSortKey] = useState('closeDate')
  const [sortAsc, setSortAsc] = useState(false)

  // Column preferences
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE)
  const [columnOrder, setColumnOrder] = useState(DEFAULT_ORDER)
  const [colPrefsLoaded, setColPrefsLoaded] = useState(false)
  const [showColDropdown, setShowColDropdown] = useState(false)

  // Slide-over
  const [selectedTradeId, setSelectedTradeId] = useState(null)
  const [selectedSetupId, setSelectedSetupId] = useState(null)
  const selectedSetup = groups.find(group => group.id === selectedSetupId)

  // Preferences are optional; history is loaded separately and fails closed.
  useEffect(() => {
    let active = true
    setSelectedTradeId(null)
    setSelectedSetupId(null)
    setSelectedAccounts([])
    setColPrefsLoaded(false)
    setVisibleColumns(DEFAULT_VISIBLE)
    setColumnOrder(DEFAULT_ORDER)
    if (!user) return
    async function load() {
      const { data: prefs } = await supabase.from('trade_log_preferences')
        .select('visible_columns, column_order').eq('user_id', user.id).maybeSingle()
      if (!active) return
      if (prefs && Array.isArray(prefs.visible_columns) && Array.isArray(prefs.column_order)) {
        const validKeys = new Set(ALL_COLUMNS.map(c => c.key))
        const visible = prefs.visible_columns.filter(k => validKeys.has(k))
        const order = prefs.column_order.filter(k => validKeys.has(k))
        if (visible.length) setVisibleColumns(visible)
        if (order.length) setColumnOrder([...order, ...DEFAULT_ORDER.filter(k => !order.includes(k))])
      }
      setColPrefsLoaded(true)
    }
    load()
    return () => { active = false }
  }, [user?.id])

  // Persist column preferences (debounced 1s)
  const saveTimer = useRef(null)
  useEffect(() => {
    if (!user || !colPrefsLoaded) return
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      supabase.from('trade_log_preferences').upsert({
        user_id: user.id,
        visible_columns: visibleColumns,
        column_order: columnOrder,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' })
    }, 1000)
    return () => clearTimeout(saveTimer.current)
  }, [visibleColumns, columnOrder, user, colPrefsLoaded])

  // Active columns = ordered + visible
  const activeColumns = useMemo(() => {
    return columnOrder
      .filter(key => visibleColumns.includes(key))
      .map(key => COL_MAP[key])
      .filter(Boolean)
  }, [visibleColumns, columnOrder])

  // Unique accounts
  const accounts = useMemo(() => {
    const set = new Set(trades.map(t => t.account_number))
    return [...set].sort()
  }, [trades])

  // Compute effective date range from preset or custom
  const [effStart, effEnd] = useMemo(() => {
    if (datePreset === 'custom') return [dateStart, dateEnd]
    if (datePreset === 'all') return ['', '']
    return getDateRange(datePreset)
  }, [datePreset, dateStart, dateEnd])

  // Filter — date range matches entry_date OR exit_date
  const filtered = useMemo(() => {
    return (reviewView === 'setups' ? groups : unresolved).filter(t => {
      if (effStart || effEnd) {
        const entry = t.entry_date || ''
        const exit = t.exit_date || ''
        const entryInRange = (!effStart || entry >= effStart) && (!effEnd || entry <= effEnd)
        const exitInRange = exit && (!effStart || exit >= effStart) && (!effEnd || exit <= effEnd)
        if (!entryInRange && !exitInRange) return false
      }
      if (selectedAccounts.length > 0 && !(t.accounts || [{ account_number: t.account_number }]).some(a => selectedAccounts.includes(a.account_number))) return false
      if (sideFilter !== 'all' && t.side !== sideFilter) return false
      if (statusFilter === 'open' && t.status !== 'open' && t.status !== 'partial') return false
      if (statusFilter === 'closed' && t.status !== 'closed') return false
      if (typeFilter !== 'all' && t.asset_type !== typeFilter) return false
      return true
    })
  }, [groups, unresolved, reviewView, effStart, effEnd, selectedAccounts, sideFilter, statusFilter, typeFilter])

  // Missing dates never jump above dated trades; entry_time breaks ties.
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = getSortValue(a, sortKey)
      const bv = getSortValue(b, sortKey)
      if (sortKey === 'closeDate' || sortKey === 'openDate') {
        if (av === '' && bv !== '') return 1
        if (av !== '' && bv === '') return -1
      }
      if (av < bv) return sortAsc ? -1 : 1
      if (av > bv) return sortAsc ? 1 : -1
      // Tiebreaker: sort by entry_time DESC (most recent first)
      const atTime = a.entry_time || ''
      const btTime = b.entry_time || ''
      if (atTime > btTime) return -1
      if (atTime < btTime) return 1
      return 0
    })
  }, [filtered, sortKey, sortAsc])

  function handleSort(key) {
    if (sortKey === key) setSortAsc(a => !a)
    else { setSortKey(key); setSortAsc(true) }
  }

  function toggleAccount(acct) {
    setSelectedAccounts(prev =>
      prev.includes(acct) ? prev.filter(a => a !== acct) : [...prev, acct]
    )
  }

  function acctLabel(num) {
    return nicknameMap[num] || ('···' + String(num || '').slice(-4))
  }

  // Only complete, internally balanced closed setups contribute to statistics.
  const stats = setupTradeStats(filtered)
  const totalNetPnl = stats.netPnl
  const totalFees = filtered.filter(t => t.performanceEligible)
    .reduce((s, t) => s + Number(t.commission) + Number(t.fees), 0)
  const winCount = stats.wins
  const lossCount = stats.losses
  const beCount = stats.breakeven

  // Cell renderer
  function renderCell(key, trade) {
    const pnl = Number(trade.net_pnl)
    const isWin = pnl > 0
    const isLoss = pnl < 0
    const isOption = trade.asset_type === 'option'
    switch (key) {
      case 'status': {
        const st = trade.status
        if (st === 'unresolved') return <span className="text-xs text-ember">NEEDS REVIEW</span>
        if (st === 'open' || st === 'partial') {
          return <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-canvas2 text-inkMuted border border-border">{st === 'partial' ? 'PARTIAL' : 'OPEN'}</span>
        }
        // closed
        if (trade.net_pnl == null) {
          return <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-canvas2 text-inkMuted border border-border">CLOSED</span>
        }
        return (
          <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full font-semibold ${
            isWin ? 'bg-forest text-white' : isLoss ? 'bg-ember text-white' : 'bg-canvas2 text-inkMuted border border-border'
          }`}>
            {trade.project ? (isWin ? 'WIN' : isLoss ? 'LOSS' : 'BE') : 'UNATTRIBUTED'}
          </span>
        )
      }
      case 'account':
        return <span className="font-mono text-xs text-inkMuted whitespace-nowrap">{trade.accounts ? `${trade.accounts.length} accounts` : acctLabel(trade.account_number)}</span>
      case 'symbol': {
        const sym = isOption ? formatOCC(trade.symbol) : (trade.symbol || '—')
        return <div><span className="font-black text-xs tracking-wide whitespace-nowrap">{sym}</span><div className="text-xs text-inkMuted">{trade.setupLabel || 'Setup link to review'}</div>{trade.attributionReason && <div className="text-xs text-ember max-w-xs">{trade.attributionReason}</div>}{trade.dataIssues?.length > 0 && <div className="text-xs text-ember">Excluded from setup statistics · see details</div>}</div>
      }
      case 'side':
        return <span className={`font-mono text-[10px] font-semibold ${trade.side === 'long' ? 'text-forest' : 'text-ember'}`}>{trade.side?.toUpperCase() || '—'}</span>
      case 'entry':
        return <span className="font-mono text-xs"><PrivateValue>{fmtCurrency(trade.avg_entry_price)}</PrivateValue></span>
      case 'exit':
        return <span className="font-mono text-xs"><PrivateValue>{fmtCurrency(trade.avg_exit_price)}</PrivateValue></span>
      case 'openDate':
        return <span className="font-mono text-[11px] text-inkMuted whitespace-nowrap">{fmtDate(trade.entry_date, trade.entry_time, userTz, trade.entry_at)}</span>
      case 'closeDate':
        return <span className="font-mono text-[11px] text-inkMuted whitespace-nowrap">{fmtDate(trade.exit_date, trade.exit_time, userTz, trade.exit_at)}</span>
      case 'qty':
        return <span className="font-mono text-xs"><PrivateValue>{trade.quantity_total != null ? Number(trade.quantity_total) : '—'}</PrivateValue></span>
      case 'netPnl':
        return <span className={`font-mono text-xs font-semibold ${clr(pnl)}`}><PrivateValue>{trade.net_pnl != null ? (pnl >= 0 ? '+' : '') + fmtCurrency(pnl) : '—'}</PrivateValue></span>
      case 'netRoi':
        return <span className={`font-mono text-xs ${clr(Number(trade.pnl_percent))}`}><PrivateValue>{fmtPct(trade.pnl_percent)}</PrivateValue></span>
      case 'fees': {
        const f = trade.commission == null || trade.fees == null ? null : Number(trade.commission) + Number(trade.fees)
        return <span className="font-mono text-xs text-inkMuted"><PrivateValue>{fmtCurrency(f)}</PrivateValue></span>
      }
      case 'stopLoss':
        return <span className="font-mono text-xs"><PrivateValue>{fmtCurrency(trade.stop_loss)}</PrivateValue></span>
      case 'risk':
        return <span className="font-mono text-xs text-ember"><PrivateValue>{fmtCurrency(trade.risk_amount)}</PrivateValue></span>
      case 'rMultiple': {
        const r = trade.r_multiple != null ? Number(trade.r_multiple) : null
        return <span className={`font-mono text-xs ${r != null ? clr(r) : 'text-inkMuted'}`}>{r != null ? r.toFixed(2) : '—'}</span>
      }
      case 'duration':
        return <span className="font-mono text-xs text-inkMuted">{fmtDuration(trade.duration_minutes)}</span>
      case 'type':
        return <span className="font-mono text-xs text-inkMuted">{trade.asset_type === 'etf' ? 'ETF' : trade.asset_type ? trade.asset_type.charAt(0).toUpperCase() + trade.asset_type.slice(1) : '—'}</span>
      default:
        return null
    }
  }

  // Footer cell
  function renderFooterCell(key, isFirst) {
    if (isFirst) return <span className="font-mono text-[10px] uppercase tracking-widest text-inkMuted font-semibold">Included in statistics</span>
    switch (key) {
      case 'qty':
        return <span className="font-mono text-xs font-semibold text-inkMuted">{winCount}W / {lossCount}L / {beCount}BE</span>
      case 'netPnl':
        return <span className={`font-mono text-xs font-bold ${clr(totalNetPnl)}`}><PrivateValue>{totalNetPnl == null ? '—' : (totalNetPnl >= 0 ? '+' : '') + fmtCurrency(totalNetPnl)}</PrivateValue></span>
      case 'fees':
        return <span className="font-mono text-xs font-semibold text-inkMuted"><PrivateValue>{fmtCurrency(stats.count ? totalFees : null)}</PrivateValue></span>
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Positions</div>
        <h1 className="font-black text-2xl uppercase tracking-wide text-ink">Trade Log</h1>
      </div>

      {/* KPI Cards */}
      <div className="flex flex-wrap gap-2">
        <Pill label={`Setups (${groups.length})`} active={reviewView === 'setups'} onClick={() => { setReviewView('setups'); setSelectedTradeId(null) }} />
        <Pill label={`Setup links to review (${unresolved.length})`} active={reviewView === 'unresolved'} onClick={() => { setReviewView('unresolved'); setSelectedSetupId(null) }} />
        <button className="text-xs underline ml-auto" onClick={reload}>Refresh history</button>
      </div>
      <p className="text-xs text-inkMuted">One row per setup across accounts. Statistics include only closed setups whose recorded fills, quantities, fees and P&amp;L agree. Account-statement reconciliation is a separate check.</p>
      <p className="text-xs text-inkMuted">Dates select setups opened or closed in the range using Chicago dates; amounts cover their full recorded lifetime. Account filters select whole setups and retain all participating accounts.</p>
      <KPICards trades={filtered} unavailable={loading || !!error} />
      {loading && <p role="status" className="text-sm text-inkMuted">Loading complete trade history…</p>}
      {error && <p role="alert" className="text-sm text-ember">{error} Statistics are unavailable.</p>}

      {reviewView === 'unresolved' && <p className="text-sm text-inkMuted">These account trades cannot yet be included in a verified setup group. A missing setup link does not mean the trade’s cashflows are unreconciled. Each row explains the link or execution check that remains; these records stay outside setup statistics.</p>}

      {/* Filter Bar */}
      <div className="bg-canvas border border-border rounded-xl px-4 py-3 space-y-3">
        {/* Date range presets */}
        <div className="flex flex-wrap items-center gap-1.5">
          {DATE_PRESETS.map(p => (
            <Pill key={p.key} label={p.label} active={datePreset === p.key} onClick={() => setDatePreset(p.key)} />
          ))}
          {datePreset === 'custom' && (
            <div className="flex items-center gap-1.5 ml-2">
              <input type="date" value={dateStart} onChange={e => { setDateStart(e.target.value); setDatePreset('custom') }}
                className="font-mono text-[11px] px-2 py-1 rounded-lg bg-canvas2 border border-border text-ink w-[130px]" />
              <span className="text-inkMuted text-xs">—</span>
              <input type="date" value={dateEnd} onChange={e => { setDateEnd(e.target.value); setDatePreset('custom') }}
                className="font-mono text-[11px] px-2 py-1 rounded-lg bg-canvas2 border border-border text-ink w-[130px]" />
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Side */}
          <div className="flex items-center gap-1">
            {['all', 'long', 'short'].map(s => (
              <Pill key={s} label={s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)} active={sideFilter === s} onClick={() => setSideFilter(s)} />
            ))}
          </div>

          <div className="w-px h-6 bg-border hidden sm:block" />

          {/* Status */}
          <div className="flex items-center gap-1">
            {['all', 'open', 'closed'].map(s => (
              <Pill key={s} label={s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)} active={statusFilter === s} onClick={() => setStatusFilter(s)} />
            ))}
          </div>

          <div className="w-px h-6 bg-border hidden sm:block" />

          {/* Type */}
          <div className="flex items-center gap-1">
            {['all', 'equity', 'etf', 'option', 'future'].map(s => (
              <Pill key={s} label={s === 'all' ? 'All' : s === 'etf' ? 'ETF' : s.charAt(0).toUpperCase() + s.slice(1)} active={typeFilter === s} onClick={() => setTypeFilter(s)} />
            ))}
          </div>

          {/* Columns button */}
          <div className="ml-auto relative">
            <button
              onClick={() => setShowColDropdown(v => !v)}
              className={`font-mono text-[10px] px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                showColDropdown
                  ? 'bg-forest text-white border-forest'
                  : 'bg-canvas2 text-inkMuted border-border hover:text-ink'
              }`}
            >
              <SlidersHorizontal size={11} />
              Columns
            </button>
            {showColDropdown && (
              <ColumnsDropdown
                columnOrder={columnOrder}
                visibleColumns={visibleColumns}
                setColumnOrder={setColumnOrder}
                setVisibleColumns={setVisibleColumns}
                onClose={() => setShowColDropdown(false)}
              />
            )}
          </div>
        </div>

        {/* Account pills */}
        {accounts.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-[10px] text-inkMuted uppercase tracking-widest mr-1">Accounts:</span>
            {accounts.map(a => (
              <Pill key={a} label={acctLabel(a)} active={selectedAccounts.includes(a)} onClick={() => toggleAccount(a)} />
            ))}
            {selectedAccounts.length > 0 && (
              <button onClick={() => setSelectedAccounts([])} className="font-mono text-[10px] text-inkMuted hover:text-ink underline ml-1">
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Trade Table — full-width breakout */}
      {loading || error ? null : sorted.length === 0 ? (
        <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
          <FileX2 size={28} className="text-border mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-mono text-xs text-inkMuted">{reviewView === 'setups' ? 'No attributed setups match these filters. Check Setup links to review for account records.' : 'No account records need setup-link review in these filters.'}</div>
        </div>
      ) : (
        <div className="w-[100vw] relative left-1/2 -translate-x-1/2 px-4 md:px-6">
          <div className="bg-canvas border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-canvas">
                  <tr className="border-b-2 border-border">
                    {activeColumns.map(col => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className={`px-3 py-2.5 font-mono text-[10px] tracking-widest uppercase text-inkMuted cursor-pointer hover:text-ink transition-colors whitespace-nowrap select-none ${
                          col.align === 'right' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {col.label}
                        {sortKey === col.key && (
                          <span className="ml-1 text-ink">{sortAsc ? '▲' : '▼'}</span>
                        )}
                      </th>
                    ))}
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((trade, i) => {
                    const projectId = trade.project?.id || projectsByTradeId[trade.id]
                    return (
                      <tr
                        key={trade.id}
                        onClick={() => trade.project ? setSelectedSetupId(trade.id) : setSelectedTradeId(trade.id)}
                        className={`border-b border-border last:border-0 hover:bg-canvas2/60 transition-colors cursor-pointer ${i % 2 === 0 ? '' : 'bg-canvas2/20'}`}
                      >
                        {activeColumns.map(col => (
                          <td key={col.key} className={`px-3 py-2.5 ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                            {renderCell(col.key, trade)}
                          </td>
                        ))}
                        <td className="px-2 py-2.5 text-center w-8">
                          {projectId && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(buildPositionWorkspaceTarget({ symbol: trade.symbol, projectId })) }}
                              className="p-1 rounded-md text-inkDim hover:text-forest hover:bg-forest/10 transition-colors"
                              title="View Position Project"
                            >
                              <FolderKanban size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border bg-canvas2">
                    {activeColumns.map((col, idx) => (
                      <td key={col.key} className={`px-3 py-3 ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                        {renderFooterCell(col.key, idx === 0)}
                      </td>
                    ))}
                    <td className="w-8" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Trade Detail Slide-Over */}
      {selectedSetup && <TradeSlideOver setupGroup={selectedSetup} nicknameMap={nicknameMap} onClose={() => setSelectedSetupId(null)} />}
      {selectedTradeId && !loading && !error && (
        <TradeSlideOver
          tradeId={selectedTradeId}
          nicknameMap={nicknameMap}
          projectId={projectsByTradeId[selectedTradeId]}
          onClose={() => setSelectedTradeId(null)}
        />
      )}
    </div>
  )
}
